import {
  formatDateStringWithTimezone,
  getDateString,
} from "@redotech/util/date-format";
import {
  type CustomAnalyticsReportColumn,
  normalizeCustomAnalyticsReportTimestampValue,
} from "./custom-analytics-report";

const EMPTY_CELL = "---";
const DATE_PREFIX_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

export function formatCustomAnalyticsReportTableValue({
  value,
  column,
  formatCurrency,
  timezone,
}: {
  value: unknown;
  column: CustomAnalyticsReportColumn;
  formatCurrency: (amount: number | string) => string;
  timezone?: string;
}): string {
  if (value == null || value === "") {
    return EMPTY_CELL;
  }

  return formatCustomAnalyticsReportValue({
    value,
    column,
    formatCurrency,
    timezone,
  });
}

export function formatCustomAnalyticsReportCsvValue({
  value,
  column,
  formatCurrency,
  timezone,
}: {
  value: unknown;
  column: CustomAnalyticsReportColumn;
  formatCurrency: (amount: number | string) => string;
  timezone?: string;
}): string | number | boolean | null {
  if (value == null) {
    return null;
  }

  return formatCustomAnalyticsReportValue({
    value,
    column,
    formatCurrency,
    timezone,
  });
}

function formatCustomAnalyticsReportValue({
  value,
  column,
  formatCurrency,
  timezone,
}: {
  value: unknown;
  column: CustomAnalyticsReportColumn;
  formatCurrency: (amount: number | string) => string;
  timezone?: string;
}): string {
  switch (column.kind) {
    case "currency": {
      const numericValue = toNumericValue(value);
      return numericValue === null
        ? String(value)
        : formatCurrency(numericValue);
    }
    case "percent":
      return formatPercentValue(value, column.format?.decimalPlaces ?? null);
    case "integer": {
      const numericValue = toNumericValue(value);
      return numericValue === null
        ? String(value)
        : Math.round(numericValue).toLocaleString();
    }
    case "number": {
      const numericValue = toNumericValue(value);
      return numericValue === null
        ? String(value)
        : formatNumberValue(numericValue, column.format?.decimalPlaces ?? null);
    }
    case "date":
      return formatDateOnlyValue(value);
    case "datetime":
      return formatDateStringWithTimezone(normalizeDateValue(value), timezone);
    case "boolean":
      return normalizeBooleanValue(value) ? "Yes" : "No";
    case "json":
      if (isScalarArray(value)) {
        return value.map(String).join(";");
      }
      return typeof value === "string" ? value : JSON.stringify(value);
    case "text":
      return Array.isArray(value) ? value.join(", ") : String(value);
  }
}

function isScalarArray(value: unknown): value is (string | number | boolean)[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item === "string" ||
        typeof item === "number" ||
        typeof item === "boolean",
    )
  );
}

function toNumericValue(value: unknown): number | null {
  const numericValue = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function formatNumberValue(
  value: number,
  decimalPlaces: number | null,
): string {
  if (decimalPlaces === null) {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
}

function formatPercentValue(
  value: unknown,
  decimalPlaces: number | null,
): string {
  const numericValue = toNumericValue(value);
  if (numericValue === null) {
    return String(value);
  }
  const percentValue =
    Math.abs(numericValue) <= 1 ? numericValue * 100 : numericValue;
  return `${formatNumberValue(percentValue, decimalPlaces)}%`;
}

function normalizeBooleanValue(value: unknown): boolean {
  if (typeof value === "string") {
    return value.toLowerCase() === "true";
  }
  return Boolean(value);
}

function normalizeDateValue(value: unknown): string | Date {
  if (value instanceof Date) {
    return value;
  }
  return normalizeCustomAnalyticsReportTimestampValue(String(value));
}

function formatDateOnlyValue(value: unknown): string {
  const dateParts = getUtcDateParts(value);
  if (dateParts === null) {
    return String(value);
  }

  const [year, month, day] = dateParts;
  return getDateString(new Date(Date.UTC(year, month - 1, day)), {
    ianaTimezone: "UTC",
    locale: "en-US",
  });
}

function getUtcDateParts(value: unknown): [number, number, number] | null {
  if (value instanceof Date) {
    return [
      value.getUTCFullYear(),
      value.getUTCMonth() + 1,
      value.getUTCDate(),
    ];
  }

  const stringValue = String(value).trim();
  const datePrefixMatch = DATE_PREFIX_PATTERN.exec(stringValue);
  if (datePrefixMatch) {
    return [
      Number(datePrefixMatch[1]),
      Number(datePrefixMatch[2]),
      Number(datePrefixMatch[3]),
    ];
  }

  const parsedDate = new Date(
    normalizeCustomAnalyticsReportTimestampValue(stringValue),
  );
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return [
    parsedDate.getUTCFullYear(),
    parsedDate.getUTCMonth() + 1,
    parsedDate.getUTCDate(),
  ];
}
